import { styled } from '@material-ui/styles';
import { getMapItemsByType } from 'Plugins/FloorPlan/Utils/Room/MapByType';
import React, { forwardRef } from 'react';

import { Logger } from '../../../../Errors/Logger';
import { StringKey } from '../../../../Locales/fr';
import { Message } from '../../../../Locales/I18nService';
import { LogLevelType } from '../../../../Services/Analytics/LogLevelType';
import { RoomType } from '../../Models/IRoom';
import { RawBoxRoomsRibs } from '../Widgets/BoxWalls/BoxWallRibs';
import { RawBoxWalls } from '../Widgets/BoxWalls/BoxWalls';
import { RawCarpentryItems } from '../Widgets/Openings/BoxCarpentryItems';
import { RawBoxRoomItems } from '../Widgets/RoomItems/BoxRoomItems';
import { RawBoxAreas } from '../Widgets/Rooms/BoxAreas';
import { calculateMapBox, ExportMapSvgProps, ExportMapViewProps, MapLegend, toShrinkScale } from './ExportMapSvg';
import { ExportMapImageHelper } from './Helpers/ExportMapPngHelper';
import { SvgHelpers } from './Helpers/SvgGenerationHelpers';
import { MapHelper } from 'Plugins/FloorPlan/Helpers/MapHelper';
import { MapUIRoomButtonFlooringDirection } from '../Laizes/Dialog/Widgets/RoomLaizeDirection/MapUIRoomsButtonFlooringDirection';
import { PolylabelUtils } from 'Plugins/FloorPlan/Utils/PolylabelUtils';
import { MapUIRoomFlooringDirection } from '../Laizes/Dialog/Widgets/RoomLaizeDirection/MapUIRoomsFlooringDirection';

const ExportMapByTypeSvgId = (type: RoomType) => `ExportMapByTypeSvg-${type}`;

export const useExportMapByTypeSvg = ({ style, mapState }: ExportMapSvgProps) => {
    const svgRoomsWrapper = React.useRef<SVGSVGElement>(null);
    const svgWallsWrapper = React.useRef<SVGSVGElement>(null);

    const assertHasRooms = (type: RoomType) => {
        return mapState.rooms?.filter((x) => x.type === type).length !== 0;
    };
    const getRef = (type: RoomType) => (type === RoomType.Sol ? svgRoomsWrapper : svgWallsWrapper);
    const captureByTypeSvg = (type: RoomType) => {
        if (!assertHasRooms(type)) {
            return '';
        }
        return SvgHelpers.capture(getRef(type).current?.outerHTML, true);
    };

    const captureByTypePng = (type: RoomType) => {
        if (!assertHasRooms(type)) {
            return Promise.resolve('');
        }
        const svgImage = captureByTypeSvg(type);
        if (!svgImage) {
            Logger.logAnalytics({
                message: `captureMapByTypePng-${type} failed`,
                level: LogLevelType.Warning,
                type: 'NULL_SVG_IMAGE',
                data: {
                    svgWrapper: getRef(type).current?.outerHTML || '-',
                    document: document.getElementById(ExportMapByTypeSvgId(type))?.outerHTML || '-',
                    roomLength: mapState.rooms?.length || 0,
                },
            });
            return Promise.resolve('');
        }
        return ExportMapImageHelper.exportMapPng(svgImage);
    };

    const { roomItems, openings, walls } = MapHelper.getRoomAllElements(mapState.rooms);

    return {
        captureByTypeSvg,
        captureByTypePng,
        mapByTypeView: (type: RoomType) => (
            <ExportMapByTypeView
                type={type}
                ref={type === RoomType.Sol ? svgRoomsWrapper : svgWallsWrapper}
                walls={walls}
                rooms={mapState.rooms}
                openings={openings}
                roomItems={roomItems}
                style={style}
            />
        ),
    };
};

const ExportMapByTypeView = forwardRef<SVGSVGElement, ExportMapViewProps>((props, ref) => {
    const { style, type, ...mapState } = props;
    const { walls, roomItems } = getMapItemsByType(mapState, type!);
    const mapBox = calculateMapBox({ walls, roomItems });
    return !mapBox ? null : (
        <SvgContainer
            id={ExportMapByTypeSvgId(type!)}
            ref={ref}
            xmlns="http://www.w3.org/2000/svg" //!\ IMPORTANT FOR EXPORT
            viewBox={`${mapBox.xMin} ${mapBox.yMin} ${mapBox.width} ${mapBox.height}`}
            width={mapBox.width}
            height={mapBox.height}
            style={style}>
            <PlanGroup {...props} legendPosition={mapBox.legendPosition} />
        </SvgContainer>
    );
});

export const SvgContainer = styled('svg')({ display: 'none' });

export const PlanGroup = ({
    style,
    showLegend = true,
    rotationAngle = 0,
    type,
    legendPosition,
    ...mapState
}: ExportMapViewProps) => {
    const { rooms, openings, roomItems, walls } = getMapItemsByType(mapState, type!);
    const overallBBox = calculateMapBox({ walls, roomItems }, rotationAngle);
    return !overallBBox ? null : (
        <g id={`planGroup-${type}`} style={style}>
            <g id="mapBoxWalls">
                <RawBoxWalls rooms={rooms} readonly />
            </g>
            <g id="mapRoomsFlooringDirection">
                {rooms.map((room) => {
                    const wall = room.flooringWallStart;
                    return !wall ? null : (
                        <MapUIRoomFlooringDirection
                            key={`roomFlooringDirection-${room.roomId}`}
                            room={room}
                            wall={wall}
                        />
                    );
                })}
            </g>
            <g id="mapBoxOpenings">
                <RawCarpentryItems openings={openings} readonly />
            </g>
            <g id="mapBoxRoomItems">
                <RawBoxRoomItems roomItems={roomItems} readonly />
            </g>
            <g id="mapBoxArea">
                <RawBoxAreas rooms={rooms} readonly />
            </g>
            <g id="mapRoomsButtonFlooringDirection">
                {rooms.map((room) => {
                    const centroid = PolylabelUtils.polylabel([room.coords?.map((p) => [p.x, p.y + 30])], 1.0);
                    return (
                        <MapUIRoomButtonFlooringDirection
                            key={`BoxArea-${room.roomId}`}
                            room={room}
                            pos={centroid}
                            readonly
                        />
                    );
                })}
            </g>
            <g id="mapBoxRibs">
                <RawBoxRoomsRibs rooms={rooms} readonly shrinkScale={toShrinkScale(overallBBox.width)} />
            </g>
            <MapLegend position={legendPosition}>
                <Message id={`LegendInfoText_${type}` as StringKey} />
            </MapLegend>
        </g>
    );
};
